import {
  Button,
  Box,
  Popover,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { BenefitSet, EnabledBenefitsEnum } from '@pxtra-client-lib-legacy/gen';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FC, useState } from 'react';
import { CheckmarkCircleFilledIcon } from '../../../../icons';
import { allBenefits } from '../../../../../stores/benefitsStore';
import CancelIcon from '@mui/icons-material/Cancel';

export const BenefitCell: FC<{ row: BenefitSet }> = ({ row }) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleBenefitsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="text"
        endIcon={<KeyboardArrowDownIcon />}
        className="flex gap-2 px-3 py-2 font-normal rounded-xl hover:bg-grey-200 transform-none"
        onClick={handleBenefitsClick}
      >
        {t('common:BENEFIT_COUNT', {
          count: row.enabled_benefits?.length
        })}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        BackdropProps={{
          invisible: true,
          style: { backgroundColor: 'transparent', opacity: '0' }
        }}
        PaperProps={{
          className: 'flex flex-col w-[350px] max-h-[420px] rounded-xl overflow-hidden  shadow-500'
        }}
      >
        <Box className="p-6 pb-3">
          <Typography className="font-bold pb-1">
            {t('common:BENEFIT_COUNT', {
              count: row.enabled_benefits?.length
            })}
          </Typography>
          <Typography className="text-grey-600 text-sm">{row.name} </Typography>
        </Box>

        <Divider />
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              display: 'flex',
              px: 2,
              py: 1.5,
              borderColor: 'divider'
            }}
          >
            <Typography variant="body2" className="text-xs font-medium text-grey-600 pl-2">
              {t('common:BENEFIT')}
            </Typography>
            <Typography
              variant="body2"
              className="text-xs text-right pr-2 font-medium text-grey-600"
              sx={{
                flex: '1 1 25%'
              }}
            >
              {t('common:ACTIVE')}
            </Typography>
          </Box>
          <List
            className="flex-1 overflow-auto py-0 mx-2"
            sx={{
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '& .MuiListItem-root:last-child': {
                mb: 0
              }
            }}
          >
            {[...allBenefits]
              .sort((a, b) => {
                const aIsEnabled =
                  row.enabled_benefits?.includes(a as EnabledBenefitsEnum) ?? false;

                const bIsEnabled =
                  row.enabled_benefits?.includes(b as EnabledBenefitsEnum) ?? false;

                if (aIsEnabled === bIsEnabled) {
                  return t(`benefits:${a}`).localeCompare(t(`benefits:${b}`));
                }

                return aIsEnabled ? -1 : 1;
              })
              .map((benefit, index) => {
                const isEnabled = row.enabled_benefits?.includes(benefit as EnabledBenefitsEnum);

                return (
                  <ListItem
                    key={benefit}
                    className="flex px-2 rounded-lg"
                    sx={{
                      backgroundColor: index % 2 === 1 ? 'grey.50' : 'transparent',
                      height: 48
                    }}
                  >
                    <ListItemText className="px-2" primary={t(`benefits:${benefit}`)} />
                    <Box className="flex justify-between pr-2">
                      {isEnabled ? (
                        <CheckmarkCircleFilledIcon className="text-success-main" />
                      ) : (
                        <CancelIcon className="text-grey-500 h-7 w-7" />
                      )}
                    </Box>
                  </ListItem>
                );
              })}
          </List>
        </Box>
      </Popover>
    </>
  );
};
